import React, {useState} from "react";
import moment from "moment";
import {message} from "antd";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {SubCompanyInvoiceFilters} from "./components/SubCompanyInvoiceFilters";
import {SubCompanyInvoiceStats} from "./components/SubCompanyInvoiceStats";
import {CompanyInvoiceTable} from "./components/CompanyInvoiceTable";
import {SubCompanyInvoiceButtonWrapper} from "./components/SubCompanyInvoiceButtonWrapper";
import {PageTitle} from "./styles/SubCompanyInvoice.styles";

const INITIAL_FILTERS = {
    dateRange: [moment().subtract(60, "days"), moment()],
    zoneId: "all",
    companyId: "all",
    subCompanyId: undefined,
    invoiceStatus: "all",
    commercialTitle: undefined,
    projectCode: undefined
};

const PAGE_SIZE = 20;

const withoutAll = value => (value && value !== "all" ? value : undefined);

const withoutBlank = value => value?.trim() || undefined;

const buildRequestParams = ({
    dateRange,
    zoneId,
    companyId,
    subCompanyId,
    invoiceStatus,
    commercialTitle,
    projectCode
}) => ({
    startDate: dateRange?.[0] ? moment(dateRange[0]).format("YYYY-MM-DD") : undefined,
    endDate: dateRange?.[1] ? moment(dateRange[1]).format("YYYY-MM-DD") : undefined,
    zoneId: withoutAll(zoneId),
    companyId: withoutAll(companyId),
    subCompanyId: withoutAll(subCompanyId),
    invoiceStatus: withoutAll(invoiceStatus),
    commercialTitle: withoutBlank(commercialTitle),
    projectCode: withoutBlank(projectCode)
});

export const SubCompanyInvoiceManagement = () => {
    const stores = useStore();
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(1);
    const requestParams = buildRequestParams(filters);
    const listParams = {...requestParams, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE};

    const handleFilterChange = (key, value) => {
        setPage(1);
        setFilters(prev => ({
            ...prev,
            [key]: value,
            // Alt firma listesi seçilen firmaya bağlı
            ...(key === "companyId" ? {subCompanyId: undefined} : {})
        }));
    };

    const [sending, setSending] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [, dashboard, , , refetchDashboard] = useFetch({
        fetchFn: () => stores.companyStore.getSubCompanyInvoiceDashboard(requestParams),
        dependencies: [JSON.stringify(requestParams)],
        defaultDataValue: {}
    });

    const [loading, invoiceList, , , refetchList] = useFetch({
        fetchFn: () => stores.companyStore.getCompanyInvoiceList(listParams),
        dependencies: [JSON.stringify(listParams)],
        defaultDataValue: {rows: [], dataCount: 0}
    });

    const [, publishNetsisInvoice] = useFetch({
        fetchFn: () => stores.appConfigStore.getAppConfig(),
        transform: response => !!response?.data?.configData?.companyInvoice?.publishNetsisInvoice,
        defaultDataValue: false
    });

    const handleSendToNetsis = async () => {
        setSending(true);
        const isSuccess = await stores.companyStore.sendSubCompanyInvoicesToNetsis(requestParams);
        setSending(false);
        // İletilen faturalar listeden düşeceği için sayı ve tablo yenilenir
        if (isSuccess) {
            refetchDashboard();
            refetchList();
        }
    };

    const handleExport = async () => {
        setExporting(true);
        const url = await stores.companyStore.exportCompanyInvoices(requestParams);
        setExporting(false);
        if (!url) {
            message.error("Excel dosyası oluşturulamadı.");
            return;
        }
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `carili-firma-faturalari-${moment().format("YYYY-MM-DD-HH-mm-ss")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div>
            <PageTitle>Carili Firma Faturaları</PageTitle>
            <SubCompanyInvoiceFilters filters={filters} onChange={handleFilterChange} />
            <SubCompanyInvoiceStats summary={dashboard} />
            <SubCompanyInvoiceButtonWrapper
                showNetsisButton={publishNetsisInvoice}
                pendingInvoiceCount={invoiceList?.pendingInvoiceCount}
                sending={sending}
                onSend={handleSendToNetsis}
                exporting={exporting}
                onExport={handleExport}
            />
            {invoiceList?.rows?.length !== 0 ? (
                <CompanyInvoiceTable
                    rows={invoiceList?.rows || []}
                    total={invoiceList?.dataCount || 0}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    loading={loading}
                    filters={requestParams}
                    fetchSubCompanies={stores.companyStore.getCompanyInvoiceSubCompanies}
                    fetchDetail={stores.companyStore.getSubCompanyInvoiceDetail}
                />
            ) : null}
        </div>
    );
};
